import React, { useContext } from 'react';
import { AuthContext, CanIAuthContext } from './context';

/**
 * The canI type definition. Declares how the function should work.
 * Pass it an ability and it will tell you if you can or cannot access the resource.
 */
export type CanIFunction<A> = (ability: keyof A) => boolean;

/**
 * This function is at the heart of the authorization logic. It is a custom react hook which can be used basically
 * everywhere underneath the Context.Provider component.
 *
 * To use the function import `useAuth` from within your component and voilà, you will get access to the `canI` function
 * and as an added bonus you also get the content of the `AuthContext` as well.
 *
 * @param context The applications authcontext created earlier with the `createAuthContext` method.
 */
export const useAuth = <Abilities>(context: React.Context<AuthContext>) => {
  const contextData = useContext(context);

  function canI(ability: keyof Abilities) {
    if (!ability) throw new Error('Ability is required');
    if (!contextData.abilities) throw new Error('Abilities are missing');
    if (!contextData.loading && !contextData.member) throw new Error('member is missing, and loading is false');
    if (!contextData.member) return false;

    const abilityFunction = contextData.abilities[ability as string];

    if (!abilityFunction) throw new Error(`'${String(ability)}' does not exist`);
    if (!contextData.user) throw new Error('User is missing');
    // TODO: exchange the checks below with whatever we will get lateron from the keycloak token
    // if (!contextData.member.memberRole) throw new Error('memberRole is missing');
    // if (!contextData.member.memberFunctions) throw new Error('memberFunctions are missing');

    return abilityFunction(contextData as CanIAuthContext);
  }

  function canISilent(ability: keyof Abilities) {
    try {
      return canI(ability);
    } catch (err) {
      return false;
    }
  }

  return { canI, canISilent, ...contextData };
};
